
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef, useState } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { featureCardStyles } from "./features/FeatureCardStyles";
import { Play } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";

const Features = () => {
  const isMobile = useIsMobile();
  const sectionRef = useRef<HTMLDivElement>(null);
  const [videoOpen, setVideoOpen] = useState(false);

  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.25, 0.8, 1.2]);
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);

  return (
    <div ref={sectionRef} className="text-white overflow-hidden h-screen flex items-center">
      <style>{featureCardStyles}</style>
      <div className="container max-w-7xl mx-auto px-4 py-8 md:py-12">
        <div className="text-center max-w-2xl mx-auto mb-8">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Do you have what it takes?</h2>
          <p className="text-white px-4 sm:px-0 leading-[1.3rem]">
            Step into the world of elite horse racing, where strategy meets speed. 
            FR:RM puts you in control of your racing destiny with an immersive management experience.
          </p>
        </div>
        
        {/* Video section with thumbnail */}
        <motion.div 
          initial={{ opacity: 0, y: 50 }} 
          whileInView={{ opacity: 1, y: 0 }} 
          transition={{ duration: 0.8 }} 
          className={`${!isMobile ? 'w-[50vw] max-h-[70vh]' : 'w-[90vw]'} mx-auto`}
        >
          <div className="relative shadow-2xl border-2 border-[#f94933]">
            <div 
              className="relative w-full aspect-video cursor-pointer group overflow-hidden"
              onClick={() => setVideoOpen(true)}
            >
              {/* Thumbnail image - Updated to use FRRM-trailer-thumbnail.webp */}
              <img 
                src="/lovable-uploads/FRRM-trailer-thumbnail.webp" 
                alt="Front Runner: Racehorse Manager Footage" 
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              
              {/* Play button overlay */}
              <div className="absolute inset-0 flex items-center justify-center bg-black/40 group-hover:bg-black/60 transition-all">
                <div className="w-16 h-16 md:w-20 md:h-20 bg-racing-accent rounded-full flex items-center justify-center transform transition-transform duration-300 group-hover:scale-110">
                  <Play className="w-8 h-8 md:w-10 md:h-10 text-white fill-white" />
                </div>
              </div>
            </div>
            
            {/* Overlapping images at bottom center */}
            <div className="absolute -bottom-12 left-1/2 -translate-x-1/2 w-32 h-20">
              <motion.img 
                initial={{ opacity: 0, scale: 0.5 }} 
                whileInView={{ opacity: 1, scale: 1 }} 
                viewport={{ once: true }} 
                transition={{ duration: 0.5, delay: 0.3, type: "spring", stiffness: 200 }} 
                src="/lovable-uploads/Star.png" 
                alt="FR:RM Star" 
                className="absolute right-0 bottom-1 w-16 h-16 object-contain" 
              />
              <motion.img 
                initial={{ opacity: 0, scale: 0.5 }} 
                whileInView={{ opacity: 1, scale: 1 }} 
                viewport={{ once: true }} 
                transition={{ duration: 0.5, type: "spring", stiffness: 200 }} 
                src="/lovable-uploads/stars2.png" 
                alt="FR:RMStars" 
                className="absolute left-0 bottom-3 w-20 h-20 object-contain z-10" 
              />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Video Dialog */}
      <Dialog open={videoOpen} onOpenChange={setVideoOpen}>
        <DialogContent className="sm:max-w-[900px] p-0 bg-transparent border-none">
          <div className="relative w-full aspect-video">
            <iframe
              className="absolute inset-0 w-full h-full"
              src="https://www.youtube.com/embed/VQaxWbFgZrA?autoplay=1&mute=0&controls=1&rel=0&modestbranding=1&showinfo=0&iv_load_policy=3"
              title="Front Runner: Racehorse Manager Game Showcase Video"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Features;
